"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
// Toast imports commented out — replaced by AddToCartModal
// import Image from "next/image";
// import Link from "next/link";
// import { getProductImageUrl } from "@/lib/utils";
// import { ShoppingCart, Check, X } from "lucide-react";
import { computeRentalTotalHt } from "@/lib/rental-pricing";

export interface CartItem {
  productId: number;
  productTitle: string;
  productSlug: string;
  productImage: string | null;
  storeName: string | null;
  categoryName: string | null;
  sellingType: "RENT" | "NEW";
  quantity: number;
  unitPriceHt: number;
  rentalDays: number | null;
  rentalStartDate: string | null;
  rentalStartTime: string | null;
  rentalEndDate: string | null;
  rentalEndTime: string | null;
  weekendPriceHt: number | null;
  isWeekendRental: boolean;
  depositPrice: number | null;
  deliveryMethod: string | null;
  freeWeekends?: boolean;
}

// interface CartNotification {
//   item: CartItem;
//   isNew: boolean;
// }

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  updateRentalDays: (productId: number, days: number) => void;
  updateRentalStartDate: (productId: number, date: string) => void;
  updateDeliveryMethod: (productId: number, method: string) => void;
  clearCart: () => void;
  cartCount: number;
  cartTotalHt: number;
  cartInsuranceHt: number;
  lastAddedItem: CartItem | null;
  dismissCartNotification: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "toolouer_cart";

function loadCartFromStorage(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<CartItem>[];
      // Rétro-compat : les paniers persistés avant l'ajout de categoryName n'ont pas ce champ
      return parsed.map((item) => ({
        categoryName: null,
        ...item,
      })) as CartItem[];
    }
  } catch (error) {
    console.error("Error loading cart from localStorage:", error);
  }
  return [];
}

function saveCartToStorage(cart: CartItem[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error("Error saving cart to localStorage:", error);
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const stored = loadCartFromStorage();
    setCart(stored);
    setIsHydrated(true);
  }, []);

  // Save cart to localStorage whenever it changes (after hydration)
  useEffect(() => {
    if (isHydrated) {
      saveCartToStorage(cart);
    }
  }, [cart, isHydrated]);

  // Last added item — exposed for AddToCartModal
  const [lastAddedItem, setLastAddedItem] = useState<CartItem | null>(null);

  const dismissCartNotification = useCallback(() => {
    setLastAddedItem(null);
  }, []);

  // Old toast notification — commented out, replaced by AddToCartModal
  // const [notification, setNotification] = useState<CartNotification | null>(null);
  // const notificationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // const showNotification = useCallback((item: CartItem, isNew: boolean) => {
  //   if (notificationTimer.current) clearTimeout(notificationTimer.current);
  //   setNotification({ item, isNew });
  //   notificationTimer.current = setTimeout(() => {
  //     setNotification(null);
  //     notificationTimer.current = null;
  //   }, 4000);
  // }, []);
  // const dismissNotification = useCallback(() => {
  //   if (notificationTimer.current) clearTimeout(notificationTimer.current);
  //   setNotification(null);
  //   notificationTimer.current = null;
  // }, []);

  const addToCart = useCallback((item: CartItem) => {
    setCart((prev) => {
      const existingIndex = prev.findIndex((i) => i.productId === item.productId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + item.quantity,
        };
        return updated;
      }
      return [...prev, item];
    });
    setLastAddedItem(item);
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    setCart((prev) => prev.filter((item) => item.productId !== productId));
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number) => {
    if (quantity < 1) return;
    setCart((prev) =>
      prev.map((item) =>
        item.productId === productId ? { ...item, quantity } : item
      )
    );
  }, []);

  const updateRentalDays = useCallback((productId: number, days: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.productId === productId ? { ...item, rentalDays: days } : item
      )
    );
  }, []);

  const updateRentalStartDate = useCallback((productId: number, date: string) => {
    setCart((prev) =>
      prev.map((item) =>
        item.productId === productId ? { ...item, rentalStartDate: date } : item
      )
    );
  }, []);

  const updateDeliveryMethod = useCallback((productId: number, method: string) => {
    setCart((prev) =>
      prev.map((item) =>
        item.productId === productId ? { ...item, deliveryMethod: method } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart]
  );

  const cartTotalHt = useMemo(
    () =>
      cart.reduce((sum, item) => {
        if (item.sellingType === "RENT" && item.rentalDays) {
          return sum + computeRentalTotalHt(
            item.unitPriceHt,
            item.weekendPriceHt ?? null,
            item.rentalDays,
            item.isWeekendRental,
            item.quantity
          );
        }
        return sum + item.unitPriceHt * item.quantity;
      }, 0),
    [cart]
  );

  // Insurance: 10% of HT for RENT items only (not subject to TVA)
  const cartInsuranceHt = useMemo(
    () =>
      cart.reduce((sum, item) => {
        if (item.sellingType !== "RENT" || !item.rentalDays) return sum;
        const itemHt = computeRentalTotalHt(
          item.unitPriceHt,
          item.weekendPriceHt ?? null,
          item.rentalDays,
          item.isWeekendRental,
          item.quantity
        );
        return sum + itemHt * 0.10;
      }, 0),
    [cart]
  );

  const value = useMemo(
    () => ({
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateRentalDays,
      updateRentalStartDate,
      updateDeliveryMethod,
      clearCart,
      cartCount,
      cartTotalHt,
      cartInsuranceHt,
      lastAddedItem,
      dismissCartNotification,
    }),
    [cart, addToCart, removeFromCart, updateQuantity, updateRentalDays, updateRentalStartDate, updateDeliveryMethod, clearCart, cartCount, cartTotalHt, cartInsuranceHt, lastAddedItem, dismissCartNotification]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      {/* Old toast notification — commented out, replaced by AddToCartModal
      {notification && (
        <div className="fixed bottom-6 right-6 z-[9999] animate-in slide-in-from-bottom-4 fade-in duration-300 max-w-sm w-full">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="bg-[#00836c] px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white text-sm font-medium">
                <Check className="h-4 w-4" />
                {notification.isNew ? "Ajouté au panier" : "Quantité mise à jour"}
              </div>
              <button onClick={dismissNotification} className="text-white/80 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 flex items-center gap-3">
              {notification.item.productImage ? (
                <div className="relative w-16 h-16 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden">
                  <Image
                    src={getProductImageUrl(notification.item.productImage)}
                    alt={notification.item.productTitle}
                    fill
                    className="object-contain p-1"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 line-clamp-2">
                  {notification.item.productTitle}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Qté : {notification.item.quantity}
                </p>
              </div>
            </div>
            <div className="px-4 pb-3">
              <Link
                href="/panier"
                onClick={dismissNotification}
                className="block w-full text-center bg-[#00836c] hover:bg-[#00836c]/90 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
              >
                Voir mon panier ({cartCount})
              </Link>
            </div>
          </div>
        </div>
      )}
      */}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
